import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/* ───────────────────────────────────────────────────────────────────
   Résolution du thème — ordre de priorité STRICT et traçable :
     1. URL  : ?theme=clair | ?theme=sombre  (déterministe : ignore cache
               ET stockage — l'outil de dépannage et de partage de lien)
     2. Choix enregistré (clé v3 — écrite uniquement par le toggle/URL)
     3. Défaut : CLAIR (design « Lawyer3 » validé)
   Historique des clés (raison du v3) :
     - « multilex-theme »    : auto-écrite 'dark' à chaque visite (jamais un choix)
     - « multilex-theme-v2 » : a pu capturer un 'dark' accidentel pendant la
                               migration (toggle cliqué au mauvais moment)
   → les deux sont IGNORÉES et nettoyées.
   La source retenue est tracée en console : [Adalia] thème: … (source: …)
   ─────────────────────────────────────────────────────────────────── */
function resoudreTheme() {
  try {
    const q = new URLSearchParams(window.location.search).get('theme');
    const parUrl = { clair: 'light', light: 'light', sombre: 'dark', dark: 'dark' }[q];
    if (parUrl) return { theme: parUrl, source: 'URL (?theme=' + q + ')' };
    const choisi = localStorage.getItem('multilex-theme-v3');
    if (choisi === 'light' || choisi === 'dark') return { theme: choisi, source: 'choix enregistré (v3)' };
  } catch (_) { /* stockage indisponible : défaut */ }
  return { theme: 'light', source: 'défaut' };
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const r = resoudreTheme();
    // eslint-disable-next-line no-console
    console.info(`[Adalia] thème: ${r.theme} (source: ${r.source})`);
    return r.theme;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('multilex-theme-v3', theme);
      // Hygiène : purge des clés héritées (sources de « dark » fantôme).
      localStorage.removeItem('multilex-theme');
      localStorage.removeItem('multilex-theme-v2');
    } catch (_) { /* noop */ }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
